'use client'

import { motion } from 'framer-motion'
import AppStoreLink from './AppStoreLink'
import ScanDemoVideo from './ScanDemoVideo'
import { track } from '@/utils/analytics'
import type { FirstScanPrompt } from '@/app/blog/firstScanPrompts'

/**
 * FirstScanCallout — the bridge from "I read the article" to "I scan the
 * product in my hand."
 *
 * Why: the previous bottom-of-article CTA was topic-aware ("track your
 * water sources over time") but not article-aware. A reader of the K-Cups
 * piece wants to scan their K-Cup pack, not see a generic prompt. This
 * component takes a per-slug `FirstScanPrompt` from firstScanPrompts.ts
 * and renders a callout that:
 *
 *  - Names the specific product to scan ("scan your K-Cup pack")
 *  - Lists matching brand examples so the reader recognises what to grab
 *  - Pairs with the inline ScanDemoVideo so they see exactly what they'll
 *    be doing in 30 seconds
 *  - Single CTA: get the app and do the scan
 *
 * Layout: text-left / video-right on desktop, stacked on mobile. The
 * video uses ScanDemoVideo's `inline` variant so the asset is shared
 * with the homepage hero — same network cache, same component, no
 * duplicate work.
 *
 * Tracking: `first_scan_callout_view` (impression) and the standard
 * `app_store_click` via AppStoreLink with `cta_surface=first_scan_callout`
 * so we can compare per-article install conversion to the generic
 * bottomCta baseline.
 */
export interface FirstScanCalloutProps {
  /** Per-article prompt, looked up from FIRST_SCAN_PROMPTS by slug. */
  prompt: FirstScanPrompt
  /** Post slug for analytics attribution. */
  postSlug: string
  /** Post topic for analytics attribution. */
  postTopic: string
}

export default function FirstScanCallout({
  prompt,
  postSlug,
  postTopic,
}: FirstScanCalloutProps) {
  return (
    <motion.section
      aria-labelledby="first-scan-heading"
      className="not-prose mt-12 relative overflow-hidden rounded-3xl border border-warmOrange/20 bg-gradient-to-br from-warmOrange/10 via-cream to-white p-6 sm:p-8 md:p-10"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55 }}
      onViewportEnter={() =>
        track('first_scan_callout_impression', {
          cta_surface: 'first_scan_callout',
          post_slug: postSlug,
          post_topic: postTopic,
          page_type: 'blog',
        })
      }
    >
      {/* Decorative offset accent — taste-skill: break the rectangle so
          the section reads as intentional, not generic CTA. */}
      <div
        aria-hidden="true"
        className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-warmOrange/15 blur-2xl"
      />

      <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center">
        {/* Copy + CTA */}
        <div className="md:col-span-7 order-2 md:order-1">
          <p className="text-2xs sm:text-xs font-semibold uppercase tracking-[0.22em] text-warmOrange mb-3">
            After install · scan this first
          </p>
          <h3
            id="first-scan-heading"
            className="text-[clamp(1.375rem,3.5vw,1.875rem)] font-bold text-charcoal leading-[1.15] mb-3"
          >
            Open the camera and scan <span className="text-warmOrange">{prompt.product}</span>.
          </h3>
          <p className="text-base text-gray-700 leading-relaxed mb-4">
            {prompt.payoff}
          </p>

          <div className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1 mb-6 text-sm text-gray-600">
            <span className="font-semibold text-charcoal">Try it on:</span>
            <span className="text-gray-700">{prompt.brands}</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <AppStoreLink
              source="first_scan_callout"
              ctaPosition="bottom"
              trackingParams={{
                post_slug: postSlug,
                post_topic: postTopic,
                page_type: 'blog',
                scan_target: prompt.product,
              }}
              className="inline-flex items-center justify-center bg-charcoal text-white px-6 py-3.5 rounded-control hover:bg-charcoal/90 transition-[color,background-color,border-color,text-decoration-color] duration-200 shadow-lg shadow-charcoal/20 min-h-[48px] text-base font-semibold pressable"
            >
              <svg className="w-5 h-5 mr-2.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.08 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
              {prompt.ctaLabel ?? `Get 3 free scans, start with ${prompt.product}`}
            </AppStoreLink>
            <span className="text-xs text-gray-500">
              Free · iOS · no signup wall
            </span>
          </div>
        </div>

        {/* Inline scan video */}
        <div className="md:col-span-5 order-1 md:order-2">
          <ScanDemoVideo variant="inline" surface="first_scan_callout_video" />
        </div>
      </div>
    </motion.section>
  )
}
